/**
 * OMON-343: human-readable status-line message for a failed telemetry load.
 *
 * When the per-user BigQuery daily quota is spent, omon-api answers the
 * callable protocol's RESOURCE_EXHAUSTED (429) with a user-facing message —
 * the Firebase SDK surfaces that as code "functions/resource-exhausted" and
 * puts the server's message on `.message`, so show it verbatim. Before this,
 * a failed initial load was an UNHANDLED rejection: the panel sat blank on
 * "Loading…" forever (2026-09-14 — the quota guard's own errors were
 * invisible).
 *
 * ☠️ Widened 2026-09-21. Quota was the ONLY refusal this showed honestly;
 * every other one became "check your connection and press Refresh". On
 * app.sweettreeconnect.xyz omon-api refuses every non-production caller
 * ("This service serves production data only; this account belongs to the
 * 'demo' environment") — a deliberate rule since 2026-09-15 — and the panel
 * told the person to check their wifi. It was read as a regression from that
 * day's auth changes and chased as one. A refusal is a DECISION the server
 * made and explained; retrying cannot change it, so telling someone to retry
 * is wrong twice over.
 *
 * So: when the server refused and said why, say what it said. Genuine faults
 * (network, timeout, internal error) keep the retry line, because for those
 * retrying is the right advice.
 */

/** Callable error codes that mean "the server decided no", with a reason. */
const SERVER_REFUSALS = new Set([
  "functions/permission-denied",
  "functions/unauthenticated",
  "functions/failed-precondition",
  "functions/invalid-argument",
  "functions/not-found",
  "functions/resource-exhausted",
]);

const RETRY =
  "Couldn't load sensor data — check your connection and press Refresh to retry.";

export function loadErrorMessage(err: unknown): string {
  const e = err as { code?: string; message?: string } | null;
  const code = e?.code ?? "";
  if (!SERVER_REFUSALS.has(code)) return RETRY;
  const message = e?.message?.trim() ?? "";
  // With no server message the SDK puts the bare code word on `.message`
  // ("permission-denied") — not something to show a person.
  const bare = code.replace(/^functions\//, "");
  if (message && message.toLowerCase() !== bare) return message;
  if (code === "functions/resource-exhausted") {
    return "Sensor data is over its daily query budget — try again tomorrow.";
  }
  return "You can't see sensor data for this client from this account.";
}
